/**
 * グローバルヘッダーの表示言語メニュー。
 *
 * 仕様の本文は 3 言語で揃っている（`SPEC_LOCALES`）。読み手が自分の言語を選ぶ口が無いと、
 * 揃えた訳文はブラウザの言語設定と一致する人にしか届かない。テーマトグルと同じく
 * グローバルヘッダに置き、どの画面からでも 1 手で切り替えられるようにする
 * （`docs/guidelines/frontend.md` の Major 規約）。
 *
 * 選んだ言語の永続は i18next の LanguageDetector（`caches: ['localStorage']`）が担う。
 * ここで別途 localStorage へ書くと、同じ選択が 2 箇所に載って乖離しうる。
 *
 * 一覧は `SPEC_LOCALE_CODES` から引く。ここで `['ja','en','vi']` を書くと、揃える言語の
 * 一次表現（共通部の `SPEC_LOCALES`）と 2 表現になる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business グローバルヘッダーの表示言語メニュー。読取 API と画面文言の言語をまとめて切り替える
 * @graph-connects useSpecLocale [calls] 表示中の言語の解決
 */
import { ChevronDown, Globe } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SPEC_LOCALE_CODES, SPEC_LOCALE_ENDONYMS, useSpecLocale } from '../../i18n/spec-locale.js';

/**
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 揃えている言語の一覧を出し、選ばれた言語へ画面と読取 API をまとめて切り替える
 * @graph-connects useSpecLocale [calls] 表示中の言語の解決
 */
export function LanguageMenu() {
  const { t, i18n } = useTranslation();
  const current = useSpecLocale();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        className="flex items-center gap-1 rounded-md border border-[var(--color-border)] px-2 py-2 text-sm text-[var(--color-text-primary)]"
        aria-label={t('app.language')}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Globe className="h-4 w-4" aria-hidden="true" />
        <span className="hidden sm:inline">
          {current === undefined ? t('app.language') : SPEC_LOCALE_ENDONYMS[current]}
        </span>
        <ChevronDown className="h-3 w-3" aria-hidden="true" />
      </button>

      {isOpen && (
        <>
          {/*
            画面全体を覆う透明な層で外側クリックを拾う。document への listener にしないのは、
            開閉のたびに購読を張り替える形になり、閉じ忘れが漏れとして残るためである。
          */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setIsOpen(false);
            }}
            aria-hidden="true"
          />
          <ul className="absolute right-0 z-20 mt-2 w-40 border border-[var(--color-border)] bg-[var(--color-bg-surface)] py-1">
            {SPEC_LOCALE_CODES.map((locale) => (
              <li key={locale}>
                <button
                  type="button"
                  onClick={() => {
                    void i18n.changeLanguage(locale);
                    setIsOpen(false);
                  }}
                  aria-current={locale === current}
                  className={
                    locale === current
                      ? 'w-full px-4 py-2 text-left text-sm text-[var(--color-accent-primary)]'
                      : 'w-full px-4 py-2 text-left text-sm text-[var(--color-text-secondary)]'
                  }
                >
                  {SPEC_LOCALE_ENDONYMS[locale]}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
